import { Icon } from '@iconify/react';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ModalWrapper } from 'modules/shared/components';
import { formatDate } from 'modules/shared/utils';
import type { LetterRow } from '../people.api';

/** Printed letters carry the company name, the reference and the date — nothing of the app. */
const PRINT_STYLES = `
  @page { size: A4; margin: 25mm 20mm; }
  body { font-family: Georgia, 'Times New Roman', serif; font-size: 12pt; line-height: 1.7; color: #111; }
  .head { text-align: center; border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 28px; }
  .head h1 { font-size: 16pt; margin: 0 0 4px; letter-spacing: .5px; }
  .meta { display: flex; justify-content: space-between; font-size: 10pt; color: #444; margin-bottom: 24px; }
  h2 { font-size: 13pt; margin: 0 0 20px; text-align: center; text-transform: uppercase; letter-spacing: .5px; }
  p { margin: 0 0 14px; text-align: justify; white-space: pre-wrap; }
`;

/**
 * Reads an issued letter, and prints it.
 *
 * Printing writes the letter into a hidden iframe rather than calling `window.print()` on the
 * page: the admin panel's own chrome, sidebar and table would otherwise come out with it, and
 * a letter on company paper is the whole point of the document.
 */
export default function LetterViewModal({
  letter,
  onClose,
}: {
  letter: LetterRow | null;
  onClose: () => void;
}) {
  if (!letter) return null;

  const handlePrint = () => {
    const frame = document.createElement('iframe');
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    document.body.appendChild(frame);

    const doc = frame.contentDocument;
    if (!doc) {
      document.body.removeChild(frame);
      return;
    }

    const escape = (text: string) =>
      text.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);

    const paragraphs = letter.body
      .split(/\n{2,}/)
      .map((para) => `<p>${escape(para)}</p>`)
      .join('');

    doc.open();
    doc.write(`
      <html>
        <head><title>${escape(letter.reference_no)}</title><style>${PRINT_STYLES}</style></head>
        <body>
          <div class="head"><h1>Shirke Consultants Pvt. Ltd.</h1></div>
          <div class="meta">
            <span>Ref: ${escape(letter.reference_no)}</span>
            <span>Date: ${formatDate(letter.issued_on)}</span>
          </div>
          <h2>${escape(letter.subject)}</h2>
          ${paragraphs}
        </body>
      </html>
    `);
    doc.close();

    // Give the iframe a tick to lay the document out before the print dialog opens.
    frame.contentWindow?.focus();
    setTimeout(() => {
      frame.contentWindow?.print();
      setTimeout(() => document.body.removeChild(frame), 1000);
    }, 200);
  };

  return (
    <ModalWrapper open onClose={onClose} maxWidth='md'>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' gap={2}>
        <Stack>
          <Typography sx={{ fontSize: '1.125rem', fontWeight: 600 }}>{letter.subject}</Typography>
          <Typography variant='body2' color='text.secondary'>
            {letter.letter_label} · {letter.reference_no} · {formatDate(letter.issued_on)}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {[letter.employee_name, letter.employee_code, letter.department].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
        <Button variant='outlined' startIcon={<Icon icon='mdi:printer-outline' />} onClick={handlePrint}>
          Print
        </Button>
      </Stack>

      <Divider />

      <Typography
        component='div'
        sx={{
          whiteSpace: 'pre-wrap',
          fontFamily: 'Georgia, "Times New Roman", serif',
          lineHeight: 1.8,
          overflowY: 'auto',
          padding: 1,
        }}
      >
        {letter.body}
      </Typography>

      {letter.issued_by && (
        <Typography variant='body2' color='text.secondary'>
          Issued by {letter.issued_by}
        </Typography>
      )}
    </ModalWrapper>
  );
}
